/*
  Two fixes for the add-to-cart flow on the storefront.

  1. Spinner. The theme's <product-form> puts .loading on the submit button, but
     the cart drawer app handles the submit itself, so that code never runs and
     the button gave no feedback. We mark the button on click and clear it once a
     /cart/add request settles, whichever path made it.

  2. Scroll position. The drawer app locks scrolling with `overflow-y: clip`.
     Unlike `hidden`, a clipped box is not a scroll container at all, so it
     cannot hold a scroll offset — the position is discarded and the page snaps
     to the top when the drawer opens. We watch for the app's lock class and
     pin `overflow-y: hidden` as an INLINE !important declaration, which
     outranks its stylesheet !important, then put the offset back.
*/
(function () {
  'use strict';

  var SUBMIT_SELECTOR = '[name="add"], .product-form__submit, .add-cart-btn';
  var TRIGGER_SELECTOR = SUBMIT_SELECTOR + ', .final-step__product__button';

  var root = document.documentElement;

  /* ---------- spinner ---------- */

  var pending = [];
  var pendingTimer = null;

  function markPending(button) {
    if (!button || button.disabled) return;
    // Theme and PAC forms own their request state. Pre-setting .loading here
    // previously prevented the theme's submit handler from sending the request.
    if (button.closest('product-form')) return;
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
    if (pending.indexOf(button) === -1) pending.push(button);
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(clearPending, 8000); // safety net
  }

  function clearPending() {
    clearTimeout(pendingTimer);
    pending.forEach(function (button) {
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
    });
    pending = [];
  }

  /* ---------- scroll preservation ---------- */

  var lastY = 0;
  var locked = false;

  function readY() {
    return window.pageYOffset || root.scrollTop || document.body.scrollTop || 0;
  }

  // Track the last position the shopper actually chose. Ignore readings taken
  // while the drawer is open, otherwise we'd record the reset itself.
  window.addEventListener('scroll', function () {
    if (!locked) lastY = readY();
  }, { passive: true });

  // Every app that locks scrolling does it with its own body class. Match the
  // ones on this store: the cart drawer's and Klaviyo's.
  function isScrollLocked(el) {
    var name = el && el.getAttribute && el.getAttribute('class');
    if (typeof name !== 'string') return false;
    return name.indexOf('cart-open') > -1 || name.indexOf('prevent-body-scrolling') > -1;
  }

  function eachScroller(fn) {
    fn(root);
    if (document.body) fn(document.body);
  }

  function applyLock() {
    eachScroller(function (el) {
      el.style.setProperty('overflow-y', 'hidden', 'important');
      // html has scroll-behavior:smooth, which would animate the restore.
      el.style.setProperty('scroll-behavior', 'auto', 'important');
    });
    restore();
  }

  function releaseLock() {
    eachScroller(function (el) {
      el.style.removeProperty('overflow-y');
      el.style.removeProperty('scroll-behavior');
    });
  }

  function scrollToY(y) {
    try {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, y);
    }
    if (readY() !== y) {
      root.scrollTop = y;
      if (document.body) document.body.scrollTop = y;
    }
  }

  // The app re-renders the drawer a few times as it loads sections, and can
  // clobber the position more than once, so hold it for a short window.
  function restore() {
    var target = lastY;
    if (target < 5) return;
    var deadline = Date.now() + 1200;
    (function put() {
      if (Math.abs(readY() - target) > 2) scrollToY(target);
      if (Date.now() < deadline) requestAnimationFrame(put);
    })();
  }

  function syncLockState() {
    syncDrawerRecovery();
    var open = isScrollLocked(root) || isScrollLocked(document.body);
    if (open === locked) return;
    locked = open;
    if (open) applyLock();
    else releaseLock();
  }

  /* Recover an app drawer whose read request failed after Shopify accepted an
     item. Retry only the drawer read, never the add-to-cart mutation. */
  var recoveryTimer = null;
  var drawerBusySince = 0;
  var drawerRetried = false;
  var recoveryHint = null;

  function drawerIsOpen() {
    return root.classList.contains('kaching-body__cart-open') ||
      document.body?.classList.contains('kaching-body__cart-open');
  }

  function clearRecoveryHint() {
    if (recoveryHint) recoveryHint.remove();
    recoveryHint = null;
  }

  function checkDrawerRecovery() {
    recoveryTimer = null;
    if (!drawerIsOpen()) {
      drawerBusySince = 0;
      drawerRetried = false;
      clearRecoveryHint();
      return;
    }
    var drawer = document.querySelector('.kaching-cart__inner');
    var busy = drawer && drawer.querySelector('.kaching-cart-item-skeleton, .kaching-cart__checkout-button--loading');
    if (!busy) {
      drawerBusySince = 0;
      drawerRetried = false;
      clearRecoveryHint();
    } else {
      if (!drawerBusySince) drawerBusySince = Date.now();
      var elapsed = Date.now() - drawerBusySince;
      if (elapsed >= 8000 && !drawerRetried) {
        drawerRetried = true;
        try {
          Promise.resolve(window.kachingCartApi?.refreshCart()).catch(function () {});
        } catch (error) { /* Keep the ordinary cart link available. */ }
      }
      if (elapsed >= 12000 && (!recoveryHint || !recoveryHint.isConnected)) {
        recoveryHint = document.createElement('div');
        recoveryHint.className = 'gr-cart-recovery';
        recoveryHint.setAttribute('role', 'status');
        recoveryHint.append('Your cart is taking longer to load. ');
        var cartLink = document.createElement('button');
        cartLink.type = 'button';
        cartLink.textContent = 'Open your cart';
        cartLink.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
        }, true);
        recoveryHint.append(cartLink);
        drawer.append(recoveryHint);
      }
    }
    recoveryTimer = setTimeout(checkDrawerRecovery, 500);
  }

  function syncDrawerRecovery() {
    if (!drawerIsOpen()) {
      clearTimeout(recoveryTimer);
      recoveryTimer = null;
      drawerBusySince = 0;
      drawerRetried = false;
      clearRecoveryHint();
    } else if (!recoveryTimer) {
      recoveryTimer = setTimeout(checkDrawerRecovery, 500);
    }
  }

  var observer = new MutationObserver(syncLockState);
  observer.observe(root, { attributes: true, attributeFilter: ['class'] });
  if (document.body) {
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- triggers ---------- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(TRIGGER_SELECTOR)) return;
    if (!locked) lastY = readY();
    markPending(target.closest(SUBMIT_SELECTOR));
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
    if (!locked) lastY = readY();
    markPending(form.querySelector('[type="submit"]'));
  }, true);

  /* ---------- clear the spinner once the add lands ---------- */

  function isAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') > -1;
  }

  function settle() {
    // Small delay so the spinner does not blink off before the drawer paints.
    setTimeout(clearPending, 200);
  }

  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var request = nativeFetch.apply(this, arguments);
      if (isAddUrl(url)) request.then(settle, clearPending);
      return request;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isAddUrl(url)) this.addEventListener('loadend', settle);
    return nativeOpen.apply(this, arguments);
  };
})();
